import React, { useEffect, useState } from 'react';
import api from '../../axiosConfig';
import { Search, QrCode, FileText, AlertCircle, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LiberationViewer = () => {
  const { user } = useAuth();
  const [codigo, setCodigo] = useState('');
  const [liberacion, setLiberacion] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [misCodigos, setMisCodigos] = useState([]);
  const [loadingMisCodigos, setLoadingMisCodigos] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'administrador';

  const verificarCodigo = async (codigoIngresado) => {
    if (!codigoIngresado?.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/liberaciones/verificar/${codigoIngresado.trim().toUpperCase()}`);
      setLiberacion(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Código de liberación no válido');
      setLiberacion(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificar = async (e) => {
    e.preventDefault();
    await verificarCodigo(codigo);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codigoFromUrl = params.get('codigo') || params.get('code');
    if (codigoFromUrl) {
      const normalized = codigoFromUrl.trim().toUpperCase();
      setCodigo(normalized);
      verificarCodigo(normalized);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchMisCodigos = async () => {
      try {
        setLoadingMisCodigos(true);
        const { data } = await api.get('/liberaciones/mis-codigos');
        setMisCodigos(Array.isArray(data) ? data : []);
      } catch {
        setMisCodigos([]);
      } finally {
        setLoadingMisCodigos(false);
      }
    };
    fetchMisCodigos();
  }, []);

  const handleDescargarPDF = async () => {
    if (!liberacion) return;
    const codigo = (liberacion.codigo_formateado || liberacion.codigo_unico || '').toString().trim();
    if (!codigo) return;

    try {
      setDownloading(true);
      const response = await api.get(`/liberaciones/pdf/${encodeURIComponent(codigo)}`, {
        responseType: 'blob'
      });

      const safeAsignatura = (liberacion.asignatura || 'Liberacion').toString().trim().replace(/\s+/g, '_');
      const filename = `Liberacion-${liberacion.matricula}-${safeAsignatura}.pdf`;

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('No se pudo descargar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barra superior con botón Volver */}
      <div className="bg-[#f06400] p-4 flex items-center justify-between mb-8 shadow-md">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-white hover:text-gray-200 transition-colors gap-2 font-medium"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        {liberacion && !isAdmin ? (
          <button
            type="button"
            onClick={handleDescargarPDF}
            disabled={downloading}
            className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-lg transition-colors text-sm font-bold disabled:opacity-50"
          >
            {downloading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
            Descargar PDF
          </button>
        ) : null}
      </div>

      <div className="max-w-4xl mx-auto p-6">
      {!liberacion ? (
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
          <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <QrCode className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Visualización de Liberación</h2>
          <p className="text-gray-600 mb-8">
            Ingresa tu código único de liberación o escanea tu QR para visualizar el documento oficial.
          </p>

          {!loadingMisCodigos && misCodigos.length > 0 && (
            <div className="max-w-md mx-auto mb-6 text-left">
              <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                <p className="text-xs font-black uppercase text-green-700 mb-3">
                  Código disponible
                </p>
                <div className="space-y-2">
                  {misCodigos.slice(0, 3).map((item) => (
                    <div key={`${item.codigo_unico}-${item.asignatura}-${item.periodo}`} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-green-800 font-semibold truncate">
                          {item.asignatura} · {item.periodo}
                        </p>
                        <p className="text-sm font-mono font-black text-green-900 truncate">
                          {item.codigo_formateado || item.codigo_unico}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const code = (item.codigo_formateado || item.codigo_unico || '').toString().toUpperCase();
                          setCodigo(code);
                          verificarCodigo(code);
                        }}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg transition-colors pointer-events-auto"
                      >
                        Usar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleVerificar} className="max-w-md mx-auto">
            <div className="relative mb-4">
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="Ej: LIB-A1B2C3D4"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-600 focus:ring-0 transition-all outline-none uppercase"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-700 hover:bg-red-800 text-white font-bold py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2"
            >
              {loading ? 'Verificando...' : (
                <>
                  <FileText size={20} />
                  Visualizar Documento
                </>
              )}
            </button>
          </form>

          {error && (
            <div className="mt-6 flex items-center justify-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white shadow-2xl rounded-sm p-12 border border-gray-300 relative select-none pointer-events-none" id="liberation-document">
          {/* Logos de Encabezado */}
          <img 
            src="/membrete_liberacion.png" 
            alt="Logos Institucionales" 
            className="w-full h-[85px] object-contain mb-8"
            onError={(e) => {
              const el = e.currentTarget;
              el.onerror = null;
              el.src = "/logoUNI.png";
              el.className = "w-full h-[85px] object-contain mb-8";
              el.onerror = () => {
                el.onerror = null;
                el.src = "/RENDER_UPQROO.png";
                el.className = "w-full h-[85px] object-contain mb-8";
              };
            }} 
          />

          {/* Marca de agua institucional */}
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none overflow-hidden">
            <h1 className="text-[12rem] font-black -rotate-45 tracking-widest text-gray-900">
              LIBERACION
            </h1>
          </div>

          {/* Encabezado */}
          <div className="flex justify-end items-start mb-8">
            <div className="text-right">
              <h3 className="font-bold text-sm">Universidad Politécnica de Quintana Roo</h3>
              <p className="text-sm">Departamento de Gestión Empresarial</p>
              <p className="text-xs text-gray-600 italic">Asunto: Formato de liberación de documentación</p>
              <p className="text-xs mt-1">Cancún, Quintana Roo a {new Date(liberacion.fecha_generacion).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Destinatario */}
          <div className="mb-10 mt-12">
            <h4 className="font-bold text-sm uppercase">{(liberacion.nombre_profesor || '').toUpperCase()}</h4>
            <p className="text-sm font-bold">ASESOR ACADÉMICO</p>
            <p className="text-sm font-bold">P R E S E N T E</p>
          </div>

          {/* Cuerpo */}
          <div className="text-sm leading-relaxed text-justify space-y-4 mb-12">
            <p>
              Por medio de la presente me permito informar que el estudiante{' '}
              <span className="font-bold">{liberacion.nombre_estudiante.toUpperCase()}</span> con matrícula{' '}
              <span className="font-bold">{liberacion.matricula}</span> del Programa Educativo de{' '}
              <span className="font-bold">{liberacion.programa_educativo.toUpperCase()}</span> ha concluido satisfactoriamente el procedimiento y documentación en la Plataforma en la asignatura de{' '}
              <span className="font-bold">{liberacion.asignatura.toUpperCase()}</span>
              {liberacion.empresa_nombre ? (
                <>
                  {' '}la cual realizó en la empresa{' '}
                  <span className="font-bold">{liberacion.empresa_nombre.toUpperCase()}</span>
                </>
              ) : null}
              {' '}en el periodo{' '}
              <span className="font-bold">{liberacion.periodo.toUpperCase()}</span>.
            </p>
            <p>
              Sin otro particular y agradecimiento la atención brindada al presente, hago propicia la ocasión para enviarle cordiales saludos.
            </p>
          </div>

          {/* Firma */}
          <div className="mt-24 text-center">
            <p className="text-sm mb-4">Atentamente</p>
            
            {/* Firma Personal Digital */}
            {liberacion.firma_admin_lib && (
              <div className="mb-3 flex justify-center">
                <img 
                  src={liberacion.firma_admin_lib} 
                  alt="Firma Personal" 
                  className="max-h-20 w-auto object-contain"
                />
              </div>
            )}

            <div className="w-64 border-t border-black mx-auto mb-2"></div>
            <p className="font-bold text-sm uppercase">{liberacion.nombre_admin_firma || 'LIC. DIANA PATRICIA SÁNCHEZ ZACARIAS'}</p>
            <p className="text-xs">{liberacion.cargo_admin_firma || 'Jefa del Departamento de Gestión Empresarial'}</p>
          </div>

          {/* QR y Código Único */}
          <div className="absolute bottom-12 right-12 text-center">
            <img src={liberacion.qr_data} alt="QR de Verificación" className="w-24 h-24 mx-auto mb-2" />
            <p className="text-[10px] font-mono text-gray-500">Cód: {liberacion.codigo_formateado || liberacion.codigo_unico}</p>
            {liberacion.sello_digital && (
              <p className="text-[8px] font-mono text-gray-400 mt-1 break-all w-40">
                Sello: {liberacion.sello_digital}
              </p>
            )}
          </div>

          {!isAdmin ? (
            <div className="mt-12 flex justify-center gap-3 no-print">
              <button
                type="button"
                onClick={handleDescargarPDF}
                disabled={downloading}
                className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-xs pointer-events-auto transition-colors font-bold disabled:opacity-50 inline-flex items-center gap-2"
              >
                {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                Descargar PDF
              </button>
              <button 
                type="button"
                onClick={() => setLiberacion(null)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-xs pointer-events-auto transition-colors"
              >
                Volver a buscar
              </button>
            </div>
          ) : null}
        </div>
      )}
      
      <style jsx>{`
        @media print {
          .no-print { display: none !important; }
          #liberation-document { border: none !important; shadow: none !important; }
        }
        #liberation-document {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>
      </div>
    </div>
  );
};

export default LiberationViewer;
